/**
 * Alert Monitor Hook
 * Monitors wallet transactions and triggers alerts when conditions are met
 *
 * Note: Full automatic monitoring will be implemented in Phase 2.
 * For MVP, alerts can be tested manually by checking transactions.
 */

import { useEffect } from 'react';
import { useWalletTrackerStore } from '../../store/walletTrackerStore';
import { useAlertStore } from '../../store/alertStore';

/**
 * Placeholder hook for alert monitoring
 * TODO: Implement proper monitoring without violating Rules of Hooks
 *
 * Proper implementation requires either:
 * 1. WebSocket connection for real-time monitoring
 * 2. Background polling service
 * 3. Component-based approach with dynamic hook management
 */
export function useAlertMonitor() {
  const { wallets } = useWalletTrackerStore();
  const { alerts } = useAlertStore();

  useEffect(() => {
    // Monitoring is disabled for MVP to avoid hook violations
    // Will be implemented in Phase 2 with proper architecture

    if (wallets.length > 0 && alerts.length > 0) {
      console.log(
        `[Alert Monitor] ${wallets.length} wallets and ${alerts.length} alerts configured. ` +
        `Automatic monitoring will be available in Phase 2.`
      );
    }
  }, [wallets.length, alerts.length]);
}

// Export helper functions for manual alert checking (can be used elsewhere)

/**
 * Check if transaction matches alert condition
 */
export function checkAlertCondition(
  condition: string,
  transfer: any,
  valueThreshold?: number
): boolean {
  const category = transfer.category?.toLowerCase() || '';

  switch (condition) {
    case 'any_transaction':
      return true;

    case 'swap':
      return category === 'internal' || category === 'token';

    case 'transfer_in':
      return transfer.to && category !== 'internal';

    case 'transfer_out':
      return transfer.from && category !== 'internal';

    case 'value_threshold':
      if (!valueThreshold) return false;
      const value = parseFloat(transfer.value || '0');
      const ethPrice = 2500;
      const usdValue = value * ethPrice;
      return usdValue >= valueThreshold;

    default:
      return false;
  }
}

/**
 * Create notification object from transaction
 */
export function createNotification(wallet: any, transfer: any, alert: any) {
  const value = parseFloat(transfer.value || '0');
  const ethPrice = 2500;
  const usdValue = value * ethPrice;

  let message = '';

  switch (alert.condition) {
    case 'swap':
      message = `Swapped tokens`;
      break;
    case 'transfer_in':
      message = `Received ${value.toFixed(4)} ${transfer.asset || 'ETH'}`;
      break;
    case 'transfer_out':
      message = `Sent ${value.toFixed(4)} ${transfer.asset || 'ETH'}`;
      break;
    case 'value_threshold':
      message = `Large transaction detected: $${usdValue.toLocaleString()}`;
      break;
    case 'any_transaction':
    default:
      message = `Made a transaction`;
      break;
  }

  return {
    alertId: alert.id,
    walletAddress: wallet.address,
    walletLabel: wallet.label,
    type: alert.condition,
    message,
    transactionHash: transfer.hash,
    value: usdValue > 0 ? Math.round(usdValue) : undefined,
  };
}
